import api from './api.service';
import type {
  FipeVehicle,
  MatchFilters,
  NeverTestedFipeVehicle,
  PbevVehicle,
  VehicleMatch,
  Stats,
  CreateMatchRequest,
  ManualConsumptionRecord,
  ManualConsumptionUpsertRequest,
  MarkNeverTestedRequest,
  VerifyMatchRequest,
  AutoMatchResult,
} from '@/types';

export const matchingService = {
  MAX_MATCHES_LIMIT: 1000,

  // Get statistics
  async getStats(): Promise<Stats> {
    const { data } = await api.get<Stats>('/api/stats');
    return data;
  },

  // Get unmatched FIPE vehicles
  async getUnmatchedFipe(): Promise<FipeVehicle[]> {
    const { data } = await api.get<FipeVehicle[]>('/api/unmatched/fipe');
    return data;
  },

  // Get FIPE vehicles marked as never tested on PBEV
  async getNeverTestedFipe(): Promise<NeverTestedFipeVehicle[]> {
    const { data } = await api.get<NeverTestedFipeVehicle[]>('/api/unmatched/fipe/never-tested');
    return data;
  },

  // Mark a FIPE vehicle as never tested on PBEV
  async markNeverTested(request: MarkNeverTestedRequest): Promise<NeverTestedFipeVehicle> {
    const { data } = await api.post<NeverTestedFipeVehicle>('/api/unmatched/fipe/never-tested', request);
    return data;
  },

  // Restore a FIPE vehicle to the matchable queue
  async restoreNeverTested(modelId: number): Promise<void> {
    await api.delete(`/api/unmatched/fipe/never-tested/${modelId}`);
  },

  // Get unmatched PBEV vehicles
  async getUnmatchedPbev(): Promise<PbevVehicle[]> {
    const { data } = await api.get<PbevVehicle[]>('/api/unmatched/pbev');
    return data;
  },

  // Get all matches
  async getMatches(filters: Partial<MatchFilters> = {}, limit = 1000): Promise<VehicleMatch[]> {
    const { data } = await api.get<VehicleMatch[]>('/api/matches', {
      params: {
        min_confidence: filters.minConfidence ?? 0,
        verified_state: filters.verifiedState ?? 'all',
        has_manual_consumption: filters.hasManualConsumption ?? false,
        search: filters.search || undefined,
        limit,
      },
    });
    return data;
  },

  // Get approved manual consumption override
  async getManualConsumption(modelId: number): Promise<ManualConsumptionRecord | null> {
    try {
      const { data } = await api.get<ManualConsumptionRecord>(`/api/manual-consumption/${modelId}`);
      return data;
    } catch (error) {
      if (error instanceof Error && error.message === 'Manual consumption not found') {
        return null;
      }
      throw error;
    }
  },

  // Save approved manual consumption override
  async saveManualConsumption(
    modelId: number,
    request: ManualConsumptionUpsertRequest
  ): Promise<ManualConsumptionRecord> {
    const { data } = await api.put<ManualConsumptionRecord>(`/api/manual-consumption/${modelId}`, request);
    return data;
  },

  // Get PBEV suggestions for a FIPE vehicle
  async getSuggestions(
    modelId: number,
    marca: string,
    modelo: string
  ): Promise<PbevVehicle[]> {
    const { data } = await api.get<PbevVehicle[]>('/api/pbev/suggestions', {
      params: { model_id: modelId, marca, modelo },
    });
    return data;
  },

  // Create manual match
  async createMatch(request: CreateMatchRequest): Promise<VehicleMatch> {
    const { data } = await api.post<VehicleMatch>('/api/match', request);
    return data;
  },

  // Verify/unverify match
  async verifyMatch(matchId: number, request: VerifyMatchRequest): Promise<VehicleMatch> {
    const { data } = await api.put<VehicleMatch>(`/api/match/${matchId}/verify`, request);
    return data;
  },

  // Delete match
  async deleteMatch(matchId: number): Promise<void> {
    await api.delete(`/api/match/${matchId}`);
  },

  // Run auto-matching
  async runAutoMatch(): Promise<AutoMatchResult> {
    const { data } = await api.post<AutoMatchResult>('/api/run-matching');
    return data;
  },

  // Export matches
  async exportMatches(format: 'csv' | 'json' = 'csv'): Promise<Blob | any> {
    const { data } = await api.get('/api/export/matches', {
      params: { format },
      responseType: format === 'csv' ? 'blob' : 'json',
    });
    return data;
  },
};

export default matchingService;
